import { Component, OnDestroy, OnInit, effect, inject } from '@angular/core';
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { KEYCLOAK_EVENT_SIGNAL, KeycloakEventType } from 'keycloak-angular';
import Keycloak from 'keycloak-js';
import { ToastrService } from 'ngx-toastr';
import { SessionExpiryService } from '../../services/session-expiry.service';
import { StringEmpty } from '../../utils/global-vars';

/**
 * Inactivity countdown driven by the real Keycloak session window instead of
 * the legacy `session-inactivity*` application parameters: the total window is
 * `exp - iat` of the refresh token (the realm SSO Session Idle), so changing
 * the realm settings on Keycloak is enough — no duplicated policy in the DB.
 * The final portion of the window shows a warning toast with a progress bar;
 * on timeout the server-side session is over, so the SessionExpiryService exit
 * path (blocking dialog → login) takes over instead of a silent logout.
 */
@UntilDestroy()
@Component({
  selector: 'app-idle',
  template: StringEmpty,
})
export class IdleComponent implements OnInit, OnDestroy {
  /** Longest warning countdown shown before the session ends, in seconds. */
  private static readonly MAX_WARNING_SECONDS = 60;

  private readonly keycloak = inject(Keycloak);
  private readonly sessionExpiryService = inject(SessionExpiryService);

  private idleSeconds = 0;
  private warningSeconds = 0;

  constructor(
    private readonly idle: Idle,
    private readonly translateService: TranslateService,
    private readonly toastrService: ToastrService,
  ) {
    const keycloakSignal = inject(KEYCLOAK_EVENT_SIGNAL);

    effect(() => {
      const event = keycloakSignal();

      switch (event.type) {
        case KeycloakEventType.Ready:
        case KeycloakEventType.AuthSuccess:
        case KeycloakEventType.AuthRefreshSuccess:
          this.configureFromSession();
          break;
      }
    });
  }

  public ngOnInit(): void {
    this.idle.onIdleEnd.pipe(untilDestroyed(this)).subscribe(() => {
      this.toastrService.clear();
    });

    this.idle.onTimeout.pipe(untilDestroyed(this)).subscribe(() => {
      this.toastrService.clear();
      this.sessionExpiryService.handleSessionExpired();
    });

    this.idle.onIdleStart.pipe(untilDestroyed(this)).subscribe(() => {
      this.toastrService.warning(this.translateService.instant('platform.session.idle'), StringEmpty, {
        timeOut: this.warningSeconds * 1000,
        progressBar: true,
      });
    });
  }

  public ngOnDestroy(): void {
    this.idle.stop();
  }

  private configureFromSession(): void {
    const refreshToken = this.keycloak.refreshTokenParsed;

    if (!this.keycloak.authenticated || !refreshToken?.exp || !refreshToken?.iat) {
      return;
    }

    const sessionSeconds = refreshToken.exp - refreshToken.iat;
    const warningSeconds = Math.min(IdleComponent.MAX_WARNING_SECONDS, Math.floor(sessionSeconds / 3));
    const idleSeconds = sessionSeconds - warningSeconds;

    if (idleSeconds === this.idleSeconds && warningSeconds === this.warningSeconds) {
      return;
    }

    this.idleSeconds = idleSeconds;
    this.warningSeconds = warningSeconds;

    this.idle.setIdle(idleSeconds);
    this.idle.setTimeout(warningSeconds);
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

    this.idle.stop();
    this.toastrService.clear();
    this.idle.watch();
  }
}
